import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import GroupIcon from '@mui/icons-material/Group'
import React, { useState, useRef, DragEvent } from 'react'
import useConfirmationAlert from '../../../hooks/componentHooks/useConfirmationAlert'
import { gradientColors, textColors } from '../../Common/Color/Color'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { bulkSyncTeamMembers, fetchTeamMembers } from '../../../redux/teamMember/actions'
import { refreshUserProfile } from '../../../redux/auth/actions'
import { selectSyncCounts } from '../../../redux/teamMember/selector'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'

const steps = ['Select File', 'Process File', 'Complete']

interface UploadStatus {
  updatedCount: number
  addedCount: number
  terminatedCount: number
  total: number
}

const Upload: React.FC = () => {
  const dispatch = useAppDispatch()
  const syncCounts = useAppSelector(selectSyncCounts)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [open, setOpen] = useState(false)
  const [formHasChanges, setFormHasChanges] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string>('')
  const [isDragging, setIsDragging] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [processing, setProcessing] = useState(false)
  const [teamMemberCount, setTeamMemberCount] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleOpenModal = () => {
    setOpen(true)
  }

  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()

  const handleCloseModal = async () => {
    if (formHasChanges) {
      openConfirmationAlert({
        title: 'Discard changes',
        text: 'Are you sure you want to discard your changes?',
        onConfirm: async () => {
          setOpen(false)
          resetUploadState()
          setActiveStep(0)
          // Refresh team members data when modal is closed
          await dispatch(fetchTeamMembers())
        },
      })
    } else {
      setOpen(false)
      resetUploadState()
      // Refresh team members data when modal is closed
      await dispatch(fetchTeamMembers())
    }
  }

  const resetUploadState = () => {
    setSelectedFile(null)
    setError('')
    setFormHasChanges(false)
  }

  const validateFile = (file: File): boolean => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file')
      return false
    }
    setError('')
    return true
  }

  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file)
      setFormHasChanges(true)
    } else {
      setSelectedFile(null)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const processFile = async () => {
    if (!selectedFile) return

    setProcessing(true)
    try {
      const text = await selectedFile.text()
      const lines = text.split('\n').filter((line) => line.trim()) // Remove empty lines
      const count = lines.length > 0 ? lines.length - 1 : 0 // Subtract header row
      setTeamMemberCount(count)
      setActiveStep(1)
    } catch (err) {
      setError('Error processing file')
    } finally {
      setProcessing(false)
    }
  }

  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null)

  const handleUpload = async () => {
    if (selectedFile && teamMemberCount !== null) {
      try {
        setProcessing(true)
        setError('') // Clear any previous errors
        await dispatch(bulkSyncTeamMembers(selectedFile)).unwrap()
        setUploadStatus({
          updatedCount: syncCounts.updatedCount,
          addedCount: syncCounts.addedCount,
          terminatedCount: syncCounts.terminatedCount,
          total: teamMemberCount,
        })
        setActiveStep(2)

        // Refresh user profile data after successful upload
        await dispatch(refreshUserProfile())
      } catch (err: any) {
        // Handle validation errors with detailed messages
        if (
          err.message &&
          (err.message.includes('CSV validation failed') ||
            err.message.includes('Upload Failed. Expecting the following column names'))
        ) {
          setError(err.message)
        } else if (err.message) {
          setError(`Upload failed: ${err.message}`)
        } else {
          setError('Failed to upload team members. Please try again.')
        }
        // Reset to step 0 on validation error so user can fix the file
        setActiveStep(0)
      } finally {
        setProcessing(false)
        if (activeStep === 2) {
          await dispatch(fetchTeamMembers())
        }
      }
    }
  }

  const handleNext = () => {
    if (activeStep === 0) {
      processFile()
    } else if (activeStep === 1) {
      handleUpload()
    }
  }

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ width: '100%', my: 2 }}>
            <Paper
              sx={{
                p: 3,
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type='file'
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept='.csv'
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {selectedFile ? (
                  <>
                    <InsertDriveFileIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                    <Typography variant='body1' color='textPrimary'>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant='body2' color='textSecondary'>
                      Click or drag to replace file
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                    <Typography variant='body2' color='textSecondary'>
                      Drag and drop your Roster CSV file here, or click to select
                    </Typography>
                  </>
                )}
              </Box>
            </Paper>
          </Box>
        )
      case 1:
        return (
          <Box sx={{ width: '100%', my: 2 }}>
            {processing ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', my: 4 }}>
                <GroupIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant='h6' gutterBottom>
                  Team Members Found
                </Typography>
                <Typography variant='h4' color='primary'>
                  {teamMemberCount}
                </Typography>
                <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                  Click Next to proceed with upload
                </Typography>
              </Box>
            )}
          </Box>
        )
      case 2:
        return (
          <Box sx={{ width: '100%', my: 2, textAlign: 'center' }}>
            <Typography variant='h6' gutterBottom>
              Upload Complete
            </Typography>
            {uploadStatus && (
              <>
                <Typography variant='body1' color='success.main' sx={{ mt: 1 }}>
                  Added: {syncCounts.addedCount}
                </Typography>
                <Typography variant='body1' color='info.main' sx={{ mt: 1 }}>
                  Updated: {syncCounts.updatedCount}
                </Typography>
                <Typography variant='body1' color='warning.main' sx={{ mt: 1 }}>
                  Terminated: {syncCounts.terminatedCount}
                </Typography>
                <Typography variant='body2' color='textSecondary' sx={{ mt: 2 }}>
                  Total team members in file: {uploadStatus.total}
                </Typography>
              </>
            )}
          </Box>
        )
      default:
        return null
    }
  }

  return (
    <React.Fragment>
      <Button
        variant='text'
        id='create-event-button'
        onClick={handleOpenModal}
        startIcon={<FileUploadOutlinedIcon />}
      >
        Upload CSV
      </Button>
      <Dialog open={open} fullWidth fullScreen={fullScreen} maxWidth='sm'>
        <DialogTitle
          sx={{
            background: gradientColors.greenGradient,
            color: textColors.white,
            fontWeight: 'bold',
            fontSize: '1.25rem',
          }}
        >
          Roster Upload
        </DialogTitle>
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: textColors.white,
          }}
          size='small'
        >
          {activeStep !== 2 && <CloseIcon />}
        </IconButton>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {getStepContent(activeStep)}

          {error && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'error.main',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'error.dark',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <ErrorOutlineIcon fontSize='small' sx={{ color: 'white' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant='body2'
                    component='div'
                    sx={{ whiteSpace: 'pre-line', color: 'white' }}
                  >
                    {error}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            {activeStep < steps.length - 1 && (
              <Button
                variant='contained'
                color='primary'
                onClick={handleNext}
                disabled={!selectedFile || processing}
              >
                {activeStep === 1 ? (processing ? 'Uploading...' : 'Upload') : 'Next'}
              </Button>
            )}
            {activeStep === steps.length - 1 && (
              <Button
                variant='contained'
                color='success'
                onClick={async () => {
                  setOpen(false)
                  resetUploadState()
                  setActiveStep(0)
                  // Refresh team members data when Done button is clicked
                  await dispatch(fetchTeamMembers())
                }}
              >
                Done
              </Button>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      {ConfirmationAlert}
    </React.Fragment>
  )
}

export default Upload
